"use client";

import { fetchSimBalance } from "@/hooks/useSimTokenInfo";
import { useOrdersStore } from "@/stores/ordersStore";
import { useWalletsStore } from "@/stores/walletsStore";
import { defineVoiceTool } from "realtime-voice-component";
import { z } from "zod";
import { compactBalance, compactOrder, compactWallet } from "./voiceCompact";
import { clampLimit, resolveOrder, resolveWallet } from "./voiceEntityResolvers";
import { ensurePermission, requireConfirmation } from "./voiceToolRuntime";
import { MAX_TOOL_ITEMS, orderStatusSchema, orderTypeSchema, type B3OSVoiceToolsOptions } from "./voiceToolTypes";

function summarizeBalances(balances: Awaited<ReturnType<typeof fetchSimBalance>>["balances"], limit: number) {
  const sorted = [...balances].sort((a, b) => (b.value_usd ?? 0) - (a.value_usd ?? 0)).slice(0, limit);
  const totalUsd = balances.reduce((sum, balance) => sum + (balance.value_usd ?? 0), 0);

  return {
    totalUsd,
    balances: sorted.map(compactBalance),
    count: balances.length,
  };
}

export function createWalletVoiceTools(options: B3OSVoiceToolsOptions) {
  return [
    defineVoiceTool({
      name: "list_wallets",
      description: "List organization wallets. This is read-only and never signs transactions.",
      parameters: z.object({}),
      execute: async () => {
        await useWalletsStore.getState().loadWallets();
        const wallets = useWalletsStore.getState().wallets;
        return {
          ok: true,
          wallets: wallets.map(compactWallet),
        };
      },
    }),
    defineVoiceTool({
      name: "get_wallet_balances",
      description: "Read token balances for one wallet, the default wallet, or a small set of organization wallets.",
      parameters: z.object({
        wallet: z.string().optional().describe("Wallet id, name, or address. Omit to use the default wallet."),
        allWallets: z.boolean().optional().describe("Fetch balances for up to five organization wallets."),
        chainIds: z.array(z.number().int().positive()).optional(),
        limit: z.number().int().positive().max(MAX_TOOL_ITEMS).optional(),
      }),
      execute: async ({ wallet, allWallets, chainIds, limit }) => {
        await useWalletsStore.getState().loadWallets();
        const maxBalances = clampLimit(limit);

        if (allWallets) {
          const wallets = useWalletsStore.getState().wallets.slice(0, 5);
          const results = await Promise.all(
            wallets.map(async orgWallet => {
              const balanceData = await fetchSimBalance(orgWallet.address, chainIds);
              return {
                wallet: compactWallet(orgWallet),
                ...summarizeBalances(balanceData.balances, maxBalances),
              };
            }),
          );
          return { ok: true, wallets: results };
        }

        const resolved = await resolveWallet(wallet);
        if (!resolved.ok) return resolved;
        const balanceData = await fetchSimBalance(resolved.item.address, chainIds);
        return {
          ok: true,
          wallet: compactWallet(resolved.item),
          ...summarizeBalances(balanceData.balances, maxBalances),
        };
      },
    }),
    defineVoiceTool({
      name: "start_wallet_flow",
      description:
        "Open wallet UI for guided financial flows. Sending, swapping, exporting keys, and session wallet setup stay in the interactive UI.",
      parameters: z.object({
        intent: z.enum([
          "manage",
          "transactions",
          "tokens",
          "positions",
          "orders",
          "send",
          "swap",
          "deposit",
          "export_key",
          "session_wallet",
          "auto_gas",
        ]),
        wallet: z.string().optional().describe("Wallet id, name, or address to preselect where supported."),
      }),
      execute: async ({ intent, wallet }) => {
        let walletId: string | undefined;
        if (wallet) {
          const resolved = await resolveWallet(wallet);
          if (!resolved.ok) return resolved;
          walletId = resolved.item.id;
        }

        const params = new URLSearchParams();
        if (intent === "transactions") params.set("tab", "transactions");
        if (intent === "tokens" || intent === "send" || intent === "swap" || intent === "deposit")
          params.set("tab", "tokens");
        if (intent === "positions") {
          params.set("tab", "positions");
          params.set("subtab", "polymarket");
        }
        if (intent === "orders") params.set("tab", "orders");
        if (intent === "auto_gas") params.set("tab", "auto-swap");
        if (walletId && params.get("tab") === "tokens") params.set("wallet", walletId);

        const destination = `/wallet-management${params.size ? `?${params.toString()}` : ""}`;
        options.router.push(destination);
        return {
          ok: true,
          destination,
          intent,
          walletId,
          reason:
            intent === "send" || intent === "swap" || intent === "export_key" || intent === "session_wallet"
              ? "This intent requires guided UI confirmation or signing."
              : undefined,
        };
      },
    }),
    defineVoiceTool({
      name: "list_orders",
      description: "List organization orders by status and type.",
      parameters: z.object({
        status: orderStatusSchema.optional(),
        orderType: orderTypeSchema.optional(),
        limit: z.number().int().positive().max(MAX_TOOL_ITEMS).optional(),
      }),
      execute: async ({ status, orderType, limit }) => {
        const store = useOrdersStore.getState();
        await store.fetchOrders(status, orderType);
        const orders = useOrdersStore.getState().orders.slice(0, clampLimit(limit));
        return { ok: true, orders: orders.map(compactOrder), count: orders.length };
      },
    }),
    defineVoiceTool({
      name: "order_action",
      description:
        "Cancel an order or update an order target price. Requires explicit confirmation because orders are financial.",
      parameters: z.object({
        action: z.enum(["cancel", "update_target_price"]),
        order: z.string().describe("Order id or spoken label."),
        targetPriceUsd: z.string().optional().describe("New target price in USD for update_target_price."),
      }),
      execute: async ({ action, order, targetPriceUsd }) => {
        ensurePermission(options, "canExecuteWalletActions");
        const resolved = await resolveOrder(order);
        if (!resolved.ok) return resolved;
        if (action === "update_target_price" && !targetPriceUsd) {
          return { ok: false, reason: "Updating target price requires targetPriceUsd." };
        }

        return requireConfirmation(options, {
          action: `order_action:${action}`,
          description:
            action === "cancel"
              ? `Cancel order ${resolved.item.label || resolved.item.id}`
              : `Update target price for order ${resolved.item.label || resolved.item.id} to ${targetPriceUsd}`,
          details: { action, order: compactOrder(resolved.item), targetPriceUsd },
          risk: "financial",
          title: "Confirm order action",
          successMessage: `Completed order action: ${action}`,
          execute: async () => {
            const store = useOrdersStore.getState();
            if (action === "cancel") {
              await store.cancelOrder(resolved.item.id);
              return { ok: true, action, order: compactOrder({ ...resolved.item, status: "cancelled" }) };
            }
            await store.updateTargetPrice(resolved.item.id, targetPriceUsd as string);
            return {
              ok: true,
              action,
              order: compactOrder({ ...resolved.item, targetPriceUsd: targetPriceUsd as string }),
            };
          },
        });
      },
    }),
  ];
}
